import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { type SessionKeySearchParams, restoreAppState } from '@/util/app-state';
import { removeSearchParamsFromUrl } from '@/util/http-utils';
import { keySubscriptionProducts } from '@/api/query-keys';
import { getProductCatalog } from '@/api/service/stripe.service';
import BackTo from '@/components/BackTo';
import Loading from '@/components/Loading';
import Subscription from '@/components/Subscription';
import { SubscriptionLevel } from '@/api/model/profile';
// import Authorizor from '@/util/authorizor';
import Authorized from '@/components/Authorized';
import styles from './subscribe.module.css';
import { useAuthorization, profileSelector } from '@/hooks/useAuthorization.hook';

const route = '/subscribe/';

export const Route = createFileRoute(route)({
  component: SubscribeComponent,
});

interface SubscribeSearchParams extends SessionKeySearchParams {
  back_label?: string;
  prev?: string;
}

export default function SubscribeComponent() {
  const profile = useAuthorization(profileSelector);
  const [subscriptionLevel, setSubscriptionLevel] = useState<SubscriptionLevel | undefined>();
  const { key, prev, back_label } = useSearch({ from: route }) as SubscribeSearchParams;
  const [returnPath, setReturnPath] = useState<string | (() => void) | undefined>();
  const [backToLabel, setBackToLabel] = useState<string>("Back");
    const { isLoading } = useQuery({
        queryKey: keySubscriptionProducts(), 
        queryFn: getProductCatalog
    });

  useEffect(() => {
    if (prev) {
      const path = prev;
      setReturnPath(path);
    }
    if (prev && back_label) {
      setBackToLabel(`Back to ${back_label}`)
    }
    if (key) {
      restoreAppState(key, true);
    }
    removeSearchParamsFromUrl();
  }, []);

  useEffect(() => {
    if (profile) {
      setSubscriptionLevel(profile?.subscription.type);
    }
  }, [profile]);

  const getPlanDescription = () => {
    let plan: string | undefined;
    switch (subscriptionLevel) {
      case SubscriptionLevel.STUDENT:
        plan = "Student";
        break;
      case SubscriptionLevel.PRO:
        plan = "Pro";
        break;
      case SubscriptionLevel.ORGANIZER:
        plan = "Event Organizer";
        break;
    }
    if (!plan) {
      return "Pick a plan and start your free trial today!";
    }
    return `You are subscribed to the ${plan} plan`
  };

  if (isLoading) {
    return (
      <Loading />
    );
  }

  return (
    <Authorized>
      { returnPath && (<BackTo route={returnPath} label={backToLabel} />) }
      <div className={styles.pageContainer}>
        <div className={styles.pageTitle}>
          <h2 className={styles.heading}>{getPlanDescription()}</h2>
        </div>
        <Subscription />
      </div>
    </Authorized>
  );
}
